import { ReactElement, useMemo } from "react";
import { AlertBar } from "client/Components/AlertBar/AlertBar";
import {
	Anchor,
	FormattedText,
	FormattedTextReplacement,
	FormattedTextWrappers
} from "client/Components";
import { EnabledAppToConfigure } from "./useSpaceTemplateEnabledApps";
import { localize } from "client_legacy/ndaw/culture";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import { formatList } from "client/Misc/cultureSpecificUtils";
import { Guid, newGuid } from "client_legacy/ndaw/guid";

type SpaceTemplateEnabledAppsAlertProps = {
	apps: readonly EnabledAppToConfigure[];
	onDismiss: () => void;
	onAppClick: (installedAppId: number) => void;
};

export const SpaceTemplateEnabledAppsAlert = (props: SpaceTemplateEnabledAppsAlertProps): ReactElement => {
	const {
		apps,
		onDismiss,
		onAppClick
	} = props;

	const appNamesDisplay = useMemo(
		(): AppNamesDisplay => {
			const blocks: string[] = [];
			const thingsToWrap: FormattedTextWrappers[] = [];
			const thingsToReplace: FormattedTextReplacement[] = [];

			apps.forEach((app: EnabledAppToConfigure): void => {
				const blockId: Guid = newGuid();
				blocks.push(`[[${blockId}]]{{AppName-${blockId}}}[[/${blockId}]]`);

				thingsToWrap.push({
					blockId,
					wrap: (children) => (
						<Anchor
							trackingId={generateTrackingId("app", `|${app.installedAppId}`)}
							onClick={(): void => onAppClick(ensureNotNil(app.installedAppId))}
						>
							{children}
						</Anchor>
					)
				});

				thingsToReplace.push({
					what: `{{AppName-${blockId}}}`,
					filler: app.name
				});
			});

			return {
				text: formatList(blocks),
				thingsToWrap,
				thingsToReplace
			};
		},
		[apps, onAppClick]
	);

	return (
		<AlertBar
			kind="info"
			isInline
			onDismiss={onDismiss}
			trackingId="space-template-enabled-apps-alert"
		>
			<FormattedText
				text={
					apps.length === 1
						? localize("_NewAppEnabledAlertMessage")
						: localize("_NewAppsEnabledAlertMessage")
				}
				thingsToReplace={[
					{
						what: apps.length === 1 ? "{{AppName}}" : "{{AppNames}}",
						filler: (
							<FormattedText
								text={appNamesDisplay.text}
								thingsToWrap={appNamesDisplay.thingsToWrap}
								thingsToReplace={appNamesDisplay.thingsToReplace}
							/>
						)
					}
				]}
			/>
		</AlertBar>
	);
};
SpaceTemplateEnabledAppsAlert.displayName = "SpaceTemplateEnabledAppsAlert";

type AppNamesDisplay = {
	text: string;
	thingsToWrap: FormattedTextWrappers[];
	thingsToReplace: FormattedTextReplacement[];
};